import type { AuthUser, InventoryRow } from "@apple-bee/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownToLine, ArrowUpFromLine, Plus, RotateCcw } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { api, errorMessage } from "../api";
import { EmptyState, LoadingBlock, Message } from "../components/Feedback";
import { Modal } from "../components/Modal";
import { dateTimeFormatter, formatKg } from "../format";

type Movement = {
  id: string;
  direction: "IN" | "OUT";
  quantityKg: number;
  reason: string;
  productName: string;
  warehouseName: string;
  lotCode: string;
  createdByName: string;
  createdAt: string;
  reversalOf: string | null;
};

type Reference = { id: string; name: string; code?: string; sku?: string };

export function InventoryPage({ user }: { user: AuthUser }) {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const inventoryQuery = useQuery({
    queryKey: ["inventory"],
    queryFn: () => api<{ inventory: InventoryRow[] }>("/api/inventory"),
  });
  const movementsQuery = useQuery({
    queryKey: ["inventory-movements"],
    queryFn: () =>
      api<{ movements: Movement[] }>("/api/inventory/movements?limit=30"),
  });
  const productsQuery = useQuery({
    queryKey: ["products"],
    queryFn: () => api<{ products: Reference[] }>("/api/reference/products"),
  });
  const warehousesQuery = useQuery({
    queryKey: ["warehouses"],
    queryFn: () =>
      api<{ warehouses: Reference[] }>("/api/reference/warehouses"),
  });

  const canChange = user.roles.some((role) =>
    ["OPERATIONS", "STOREKEEPER"].includes(role),
  );
  const filtered = useMemo(() => {
    const value = filter.trim().toLocaleLowerCase("ru");
    return (inventoryQuery.data?.inventory ?? []).filter((row) =>
      [row.productName, row.lotCode, row.warehouseName]
        .join(" ")
        .toLocaleLowerCase("ru")
        .includes(value),
    );
  }, [filter, inventoryQuery.data]);

  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["inventory"] }),
      queryClient.invalidateQueries({ queryKey: ["inventory-movements"] }),
      queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
    ]);
  }

  if (inventoryQuery.isLoading) return <LoadingBlock label="Сверяем остатки…" />;

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <p className="eyebrow">Складской контур</p>
          <h2>Остатки по партиям</h2>
          <p>
            Свободный остаток рассчитывается после резервов одобренных заявок.
          </p>
        </div>
        {canChange ? (
          <button
            className="button button--primary"
            type="button"
            onClick={() => setModalOpen(true)}
          >
            <Plus size={18} />
            Новое движение
          </button>
        ) : null}
      </section>

      <section className="panel">
        <div className="table-toolbar">
          <label className="search-field">
            <span>Поиск</span>
            <input
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Товар, партия или склад"
            />
          </label>
          <span className="table-toolbar__count">
            {filtered.length} поз.
          </span>
        </div>
        {filtered.length ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Товар / партия</th>
                  <th>Склад</th>
                  <th className="numeric">Физически</th>
                  <th className="numeric">В резерве</th>
                  <th className="numeric">Свободно</th>
                  <th>Обновлено</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.balanceId}>
                    <td>
                      <strong>{row.productName}</strong>
                      <span className="table-subline">{row.lotCode}</span>
                    </td>
                    <td>{row.warehouseName}</td>
                    <td className="numeric">{formatKg(row.quantityKg)}</td>
                    <td className="numeric muted">{formatKg(row.reservedKg)}</td>
                    <td className="numeric">
                      <span
                        className={`availability ${
                          row.availableKg <= 0 ? "availability--zero" : ""
                        }`}
                      >
                        {formatKg(row.availableKg)}
                      </span>
                    </td>
                    <td>{dateTimeFormatter.format(new Date(row.updatedAt))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title={filter ? "Ничего не найдено" : "Остатков пока нет"}
            description={
              filter
                ? "Измените поисковый запрос."
                : "Создайте первый приход после проверки начальных данных."
            }
          />
        )}
      </section>

      <section className="panel">
        <header className="panel__header">
          <div>
            <p className="eyebrow">Неизменяемая история</p>
            <h2>Последние движения</h2>
          </div>
        </header>
        {movementsQuery.data?.movements.length ? (
          <div className="movement-list">
            {movementsQuery.data.movements.map((movement) => (
              <MovementRow
                key={movement.id}
                movement={movement}
                canReverse={canChange}
                onReversed={refresh}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            title="Движений ещё нет"
            description="Оригинальные операции и сторно будут храниться здесь."
          />
        )}
      </section>

      {modalOpen ? (
        <MovementModal
          products={productsQuery.data?.products ?? []}
          warehouses={warehousesQuery.data?.warehouses ?? []}
          onClose={() => setModalOpen(false)}
          onCreated={async () => {
            setModalOpen(false);
            await refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function MovementRow({
  movement,
  canReverse,
  onReversed,
}: {
  movement: Movement;
  canReverse: boolean;
  onReversed: () => Promise<void>;
}) {
  const [reversing, setReversing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (reason: string) =>
      api(`/api/inventory/movements/${movement.id}/reverse`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    onSuccess: onReversed,
    onError: (caught) => setError(errorMessage(caught)),
  });

  async function reverse() {
    const reason = window.prompt("Причина сторно (не менее 5 символов):");
    if (!reason) return;
    setReversing(true);
    await mutation.mutateAsync(reason).catch(() => undefined);
    setReversing(false);
  }

  return (
    <div className="movement-row">
      <span
        className={`movement-row__icon movement-row__icon--${movement.direction.toLowerCase()}`}
      >
        {movement.direction === "IN" ? (
          <ArrowDownToLine size={18} />
        ) : (
          <ArrowUpFromLine size={18} />
        )}
      </span>
      <div className="movement-row__main">
        <strong>
          {movement.direction === "IN" ? "Приход" : "Расход"} ·{" "}
          {movement.productName}
        </strong>
        <span>
          {movement.lotCode} · {movement.warehouseName} · {movement.reason}
        </span>
        {error ? <small className="text-error">{error}</small> : null}
      </div>
      <div className="movement-row__value">
        <strong>
          {movement.direction === "IN" ? "+" : "−"}
          {formatKg(movement.quantityKg)}
        </strong>
        <span>
          {dateTimeFormatter.format(new Date(movement.createdAt))} ·{" "}
          {movement.createdByName}
        </span>
      </div>
      {canReverse && !movement.reversalOf ? (
        <button
          className="icon-button"
          type="button"
          title="Сторнировать"
          aria-label="Сторнировать движение"
          onClick={() => void reverse()}
          disabled={reversing}
        >
          <RotateCcw size={17} />
        </button>
      ) : null}
    </div>
  );
}

function MovementModal({
  products,
  warehouses,
  onClose,
  onCreated,
}: {
  products: Reference[];
  warehouses: Reference[];
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [direction, setDirection] = useState<"IN" | "OUT">("IN");
  const [productId, setProductId] = useState(products[0]?.id ?? "");
  const [warehouseId, setWarehouseId] = useState(warehouses[0]?.id ?? "");
  const [lotCode, setLotCode] = useState("");
  const [quantityKg, setQuantityKg] = useState("");
  const [harvestDate, setHarvestDate] = useState("");
  const [origin, setOrigin] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/api/inventory/movements", {
        method: "POST",
        body: JSON.stringify({
          direction,
          productId,
          warehouseId,
          lotCode,
          quantityKg,
          harvestDate: harvestDate || undefined,
          origin: origin || undefined,
          reason,
        }),
      });
      await onCreated();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Новое складское движение"
      subtitle="Операция будет сразу записана в журнал аудита."
      onClose={onClose}
    >
      <form className="modal__body form-grid" onSubmit={(event) => void submit(event)}>
        <div className="segmented span-2">
          <button
            className={direction === "IN" ? "is-active" : ""}
            type="button"
            onClick={() => setDirection("IN")}
          >
            <ArrowDownToLine size={17} /> Приход
          </button>
          <button
            className={direction === "OUT" ? "is-active" : ""}
            type="button"
            onClick={() => setDirection("OUT")}
          >
            <ArrowUpFromLine size={17} /> Расход
          </button>
        </div>
        <label>
          Товар
          <select value={productId} onChange={(event) => setProductId(event.target.value)} required>
            <option value="">Выберите товар</option>
            {products.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Склад
          <select value={warehouseId} onChange={(event) => setWarehouseId(event.target.value)} required>
            <option value="">Выберите склад</option>
            {warehouses.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Код партии
          <input value={lotCode} onChange={(event) => setLotCode(event.target.value)} placeholder="Например, 2026-07-01-A" required />
        </label>
        <label>
          Количество, кг
          <input type="number" min="0.001" step="0.001" value={quantityKg} onChange={(event) => setQuantityKg(event.target.value)} required />
        </label>
        <label>
          Дата сбора
          <input type="date" value={harvestDate} onChange={(event) => setHarvestDate(event.target.value)} />
        </label>
        <label>
          Происхождение
          <input value={origin} onChange={(event) => setOrigin(event.target.value)} placeholder="Сад / участок" />
        </label>
        <label className="span-2">
          Основание операции
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Документ или понятная причина" minLength={3} required />
        </label>
        {error ? (
          <div className="span-2">
            <Message tone="error">{error}</Message>
          </div>
        ) : null}
        <div className="modal__actions span-2">
          <button className="button button--secondary" type="button" onClick={onClose}>
            Отмена
          </button>
          <button className="button button--primary" type="submit" disabled={busy}>
            {busy ? "Сохраняем…" : "Записать движение"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

